import { useQuery } from '@tanstack/react-query';
import { cartItemApi } from '@/api/commercial/cart-items';
import { creditAnalysisApi } from '@/api/commercial/credit-analysis';
import { proposalApi } from '@/api/commercial/proposals';
import { usePaymentConfigCurrent } from './use-payment-config';

export interface PaymentFormState {
  paymentType: 'CASH' | 'INSTALLMENTS' | '';
  dueDay: string;
  installmentCount: string;
  hasEntrance: boolean;
  entrancePercent: string;
  downPaymentByBankSlip: boolean;
  prorrogarEntrada: boolean;
  entradaProrrogadaEm: string | null;
  boletoAvista: boolean;
}

export interface CartStepValidation {
  isCartValid: boolean;
  isPaymentValid: boolean;
  isStepValid: boolean;
  message: string | null;
}

const CIVIL_DATE_PATTERN = /^(\d{4})-(\d{2})-(\d{2})$/;

export function parseCivilDate(value: string): { year: number; month: number; day: number } | null {
  const match = CIVIL_DATE_PATTERN.exec(value);
  if (!match) return null;

  const year = Number(match[1]);
  const month = Number(match[2]);
  const day = Number(match[3]);
  const date = new Date(0);
  date.setHours(0, 0, 0, 0);
  date.setFullYear(year, month - 1, day);

  if (date.getFullYear() !== year || date.getMonth() !== month - 1 || date.getDate() !== day) {
    return null;
  }

  return { year, month, day };
}

export function isFutureCivilDate(value: string, today = new Date()): boolean {
  const parsed = parseCivilDate(value);
  if (!parsed) return false;
  const candidateNumber = parsed.year * 10_000 + parsed.month * 100 + parsed.day;
  const todayNumber = today.getFullYear() * 10_000 + (today.getMonth() + 1) * 100 + today.getDate();
  return candidateNumber > todayNumber;
}

export function validateCartStep(
  cartItemCount: number,
  payment: PaymentFormState,
  allowedPaymentMethods?: string[],
  minEntradaPercent?: number,
): CartStepValidation {
  const isCartValid = cartItemCount > 0;

  if (!isCartValid) {
    return {
      isCartValid: false,
      isPaymentValid: false,
      isStepValid: false,
      message: 'Adicione pelo menos um item ao carrinho',
    };
  }

  if (!payment.paymentType) {
    return {
      isCartValid: true,
      isPaymentValid: false,
      isStepValid: false,
      message: 'Selecione o tipo de pagamento',
    };
  }

  if (allowedPaymentMethods?.length && !allowedPaymentMethods.includes(payment.paymentType)) {
    return {
      isCartValid: true,
      isPaymentValid: false,
      isStepValid: false,
      message: 'Tipo de pagamento não permitido pela análise de crédito',
    };
  }

  if (payment.paymentType === 'INSTALLMENTS') {
    if (!payment.installmentCount) {
      return {
        isCartValid: true,
        isPaymentValid: false,
        isStepValid: false,
        message: 'Selecione o numero de parcelas',
      };
    }
    if (!payment.dueDay) {
      return {
        isCartValid: true,
        isPaymentValid: false,
        isStepValid: false,
        message: 'Selecione o dia de vencimento',
      };
    }
    if (payment.hasEntrance && (!payment.entrancePercent || Number(payment.entrancePercent) <= 0)) {
      return {
        isCartValid: true,
        isPaymentValid: false,
        isStepValid: false,
        message: 'Informe a porcentagem de entrada',
      };
    }
    if (payment.hasEntrance && minEntradaPercent && minEntradaPercent > 0) {
      const pct = Number(payment.entrancePercent) || 0;
      if (pct > 0 && pct < minEntradaPercent) {
        return {
          isCartValid: true,
          isPaymentValid: false,
          isStepValid: false,
          message: `Entrada mínima permitida: ${minEntradaPercent}%`,
        };
      }
    }

    if (!payment.hasEntrance && (payment.downPaymentByBankSlip || payment.prorrogarEntrada)) {
      return {
        isCartValid: true,
        isPaymentValid: false,
        isStepValid: false,
        message: 'As condições da entrada exigem uma entrada válida',
      };
    }

    if (payment.hasEntrance && payment.downPaymentByBankSlip && payment.prorrogarEntrada) {
      return {
        isCartValid: true,
        isPaymentValid: false,
        isStepValid: false,
        message: 'Boleto da entrada e prorrogação são mutuamente exclusivos',
      };
    }

    if (payment.hasEntrance && payment.prorrogarEntrada) {
      if (!payment.entradaProrrogadaEm) {
        return {
          isCartValid: true,
          isPaymentValid: false,
          isStepValid: false,
          message: 'Informe a data da entrada prorrogada',
        };
      }
      if (!isFutureCivilDate(payment.entradaProrrogadaEm)) {
        return {
          isCartValid: true,
          isPaymentValid: false,
          isStepValid: false,
          message: 'A data da entrada prorrogada deve ser futura',
        };
      }
    }
  }

  return {
    isCartValid: true,
    isPaymentValid: true,
    isStepValid: true,
    message: null,
  };
}

export function useCartStepValidation({
  proposalId,
  draftPayment,
}: {
  proposalId?: string;
  draftPayment?: PaymentFormState;
}) {
  const proposal = useQuery({
    queryKey: ['proposal', proposalId],
    queryFn: () => proposalApi.findOne(proposalId!),
    enabled: !!proposalId,
  });
  const items = useQuery({
    queryKey: ['cart-items', proposalId],
    queryFn: () => cartItemApi.findMany(proposalId!),
    enabled: !!proposalId,
  });
  const credit = useQuery({
    queryKey: ['credit-analysis', proposalId],
    queryFn: () => creditAnalysisApi.findByProposal(proposalId!),
    enabled: !!proposalId,
  });
  const config = usePaymentConfigCurrent();
  const queries = [proposal, items, credit, config];
  const isError = queries.some((query) => query.isError);
  const isReady = !!proposalId && queries.every((query) => query.isSuccess);

  if (!isReady) {
    return {
      isLoading: !!proposalId && !isError,
      isValid: false,
      message: isError ? 'Não foi possível validar o carrinho' : 'Carregando carrinho...',
    };
  }

  const payment = proposal.data?.payment;
  const validation = validateCartStep(
    items.data?.length ?? 0,
    draftPayment ?? {
      paymentType: payment?.paymentType ?? '',
      dueDay: payment?.dueDay ? String(payment.dueDay) : '',
      installmentCount: payment?.installments ? String(payment.installments) : '',
      hasEntrance: payment?.hasDownPayment ?? false,
      entrancePercent:
        payment?.downPaymentPercentage != null ? String(payment.downPaymentPercentage) : '',
      downPaymentByBankSlip: payment?.downPaymentByBankSlip ?? false,
      prorrogarEntrada: payment?.prorrogarEntrada ?? false,
      entradaProrrogadaEm: payment?.entradaProrrogadaEm ?? null,
      boletoAvista: payment?.boletoAvista ?? false,
    },
    credit.data?.allowedPaymentMethods ?? ['CASH', 'INSTALLMENTS'],
    config.data?.minEntradaPercent ?? 0,
  );

  return { isLoading: false, isValid: validation.isStepValid, message: validation.message };
}
